import { Injectable } from '@angular/core';
import { Observable, switchMap } from 'rxjs';
import { tap } from 'rxjs/operators';
import { GeneralQuery } from './general.query';
import { GeneralStore } from './general.store';
import { userMngFncList } from '../../client-um/client-um.utils';
import { AuthService } from '../../core/authentication/auth.service';
import { SparrowAppEnum } from '../../core/enums/sparrow-app.enum';
import { OperatorAttributeDto } from '../../core/models/attribute-dto';
import { ErpDto } from '../../core/models/erp-dto';
import { OrgUnitDto } from '../../core/models/org-unit-dto';
import { StockQuantityChangeReasonDto } from '../../core/models/stock-quantity-change-reason-dto';
import { UnitOfMeasureDto } from '../../core/models/unit-of-measure-dto';
import { UserDto } from '../../core/models/user-dto';
import { AttributeService } from '../../core/services/attribute.service';
import { CurrencyService } from '../../core/services/currency.service';
import { ErpService } from '../../core/services/erp.service';
import { OrgUnitsService } from '../../core/services/org-units.service';
import { UnitOfMeasureService } from '../../core/services/unit-of-measure.service';
import { UserService } from '../../core/services/user.service';
import { TreeNode } from '../../core/tree.utils';
import { compareAttrOrdering, sortUsersByName } from '../../core/utils';

@Injectable({ providedIn: 'root' })
export class GeneralService {
  constructor(
    private store: GeneralStore,
    private query: GeneralQuery,
    private authService: AuthService,
    private userService: UserService,
    private erpService: ErpService,
    private currencyService: CurrencyService,
    private orgUnitsService: OrgUnitsService,
    private attributeService: AttributeService,
    private unitOfMeasureService: UnitOfMeasureService,
  ) {}

  getAttributes(): Observable<OperatorAttributeDto[]> {
    this.store.setLoading(true);
    return this.attributeService.getOperatorAttributes().pipe(
      tap((data) => {
        this.store.setAttributeList(data);
        this.store.setLoading(false);
      }),
    );
  }

  getOperatorUsers(): Observable<UserDto[]> {
    return this.userService.operatorGetAllUsers().pipe(
      tap((data) => {
        const sortedUsers = sortUsersByName(data);
        this.store.setOperatorUsers(sortedUsers);
      }),
    );
  }

  getOrgUnits(): Observable<TreeNode<OrgUnitDto>> {
    if (this.query.getValue().orgUnits) {
      return null;
    }
    return this.orgUnitsService.getOrgUnitsByOrgId(this.authService.userWithRoles.user.organisationId).pipe(
      tap((value) => {
        this.store.setOrgUnits(value);
      }),
    );
  }

  getReductionReasons(): Observable<StockQuantityChangeReasonDto[]> {
    return this.orgUnitsService.getStockReductionReasons().pipe(
      tap((value) => {
        this.store.setReductionReasons(value);
      }),
    );
  }

  getUnitOfMeasure(): Observable<UnitOfMeasureDto[]> {
    return this.unitOfMeasureService.get().pipe(
      tap((value) => {
        this.store.setUnitOfMeasure(value);
      }),
    );
  }

  getCurrencies(): Observable<string[]> {
    return this.currencyService.get().pipe(
      tap((value) => {
        this.store.setCurrencies(value);
      }),
    );
  }

  getOperatorErp(): Observable<ErpDto[]> {
    return this.erpService.operatorGet().pipe(
      tap((value) => {
        const sortedErps = value.sort((a, b) => a.externalKey.localeCompare(b.externalKey));
        this.store.setOperatorErp(sortedErps);
        this.store.setIsOperatorMultiErp(value?.length > 1);
      }),
    );
  }

  getAccessPermissions() {
    const hasSMPermission = this.authService.userWithRoles.userRolesWithOrgUnits.some(
      (value) => value.role.sparrowApplication === SparrowAppEnum.StockManagement,
    );

    const hasCAPermission = this.authService.userWithRoles.userRolesWithOrgUnits.some(
      (value) => value.role.sparrowApplication === SparrowAppEnum.CustomerApproval,
    );

    const hasPlanPermission = this.authService.userWithRoles.userRolesWithOrgUnits.some(
      (value) => value.role.sparrowApplication === SparrowAppEnum.Planning,
    );

    const hasPoolPermission = this.authService.userWithRoles.userRolesWithOrgUnits.some(
      (value) => value.role.sparrowApplication === SparrowAppEnum.Pool,
    );

    this.store.setHasCaAccess(hasCAPermission);
    this.store.setHasSmAccess(hasSMPermission);
    this.store.setHasPlanAccess(hasPlanPermission);
    this.store.setHasPoolAccess(hasPoolPermission);
  }

  // check if user functions from user has any of the following enums in order to become root admin
  setUserPermissionAccess() {
    const userPermissionList = this.authService.userWithRoles.userRolesWithOrgUnits.filter((value) =>
      value.role.functionPermissions.some((r) => userMngFncList.includes(r.userFunction)),
    );

    this.store.setOperatorData(this.authService.userWithRoles.operator);

    this.store.setIsUserManager(userPermissionList.length > 0);
  }

  setOperatorAttributes() {
    this.authService.operatorCatalogues$
      .pipe(
        switchMap((data) => {
          const firstCatalogueId = data[0]?.id;
          return this.attributeService.getOperatorSelectionAttr(firstCatalogueId);
        }),
      )
      .subscribe((defaultCatalogueAttributes) => {
        this.store.setOperatorAttributePermission(defaultCatalogueAttributes);
        // sort by order default catalogue attributes
        const defaultAttrSort = defaultCatalogueAttributes
          .map((value) => ({
            ...value.attribute,
            readOnly: value.readOnly,
          }))
          .sort((a, b) => compareAttrOrdering(a.order, b.order));
        this.store.setCatalogueAttributes(defaultAttrSort);
      });
  }
}
